
import React, { useEffect, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Note: This component uses mock data and needs significant rework for Firebase integration
// Placeholder interfaces and functions
interface Location { id?: string; growerId: string; farmId: string; fieldId: string; defaultAssigneeId: string; }
const mockTeam: any[] = [];
const getAllGrowerOptions = () => [];
const getFarmsByGrowerId = (id: string) => [];
const getFieldsByFarmId = (id: string) => [];
const getGrowerById = (id: string) => null;
const getFarmById = (id: string) => null;
const getFieldById = (id: string) => null;
const addGrower = (data: any) => ({ id: 'temp', name: data.name });
const addFarm = (data: any) => ({ id: 'temp', name: data.name, growerId: data.growerId });
const addField = (data: any) => ({ id: 'temp', name: data.name, farmId: data.farmId, acres: data.acres });

const locationSchema = z.object({
  growerId: z.string().nonempty({ message: "Grower is required" }),
  farmId: z.string().nonempty({ message: "Farm is required" }),
  fieldId: z.string().nonempty({ message: "Field is required" }),
  defaultAssigneeId: z.string().nonempty({ message: "Default assignee is required" }),
});

interface LocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: Location;
  onSave: (location: Omit<Location, "id">) => void;
}

const LocationDialog: React.FC<LocationDialogProps> = ({ open, onOpenChange, location, onSave }) => {
  const [availableFarms, setAvailableFarms] = useState<ReturnType<typeof getFarmsByGrowerId>>([]);
  const [availableFields, setAvailableFields] = useState<ReturnType<typeof getFieldsByFarmId>>([]);
  
  // States for new entry creation
  const [showNewGrowerInput, setShowNewGrowerInput] = useState(false);
  const [newGrowerName, setNewGrowerName] = useState("");
  
  const [showNewFarmInput, setShowNewFarmInput] = useState(false);
  const [newFarmName, setNewFarmName] = useState("");
  
  const [showNewFieldInput, setShowNewFieldInput] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldAcres, setNewFieldAcres] = useState<string>("0");
  
  const form = useForm<z.infer<typeof locationSchema>>({
    resolver: zodResolver(locationSchema),
    defaultValues: {
      growerId: location?.growerId || "",
      farmId: location?.farmId || "",
      fieldId: location?.fieldId || "",
      defaultAssigneeId: location?.defaultAssigneeId || "",
    },
  });
  
  // Update form values when location changes
  useEffect(() => {
    if (location) {
      form.reset({
        growerId: location.growerId,
        farmId: location.farmId,
        fieldId: location.fieldId,
        defaultAssigneeId: location.defaultAssigneeId,
      });
      
      // Set available farms and fields based on selected grower and farm
      setAvailableFarms(getFarmsByGrowerId(location.growerId));
      setAvailableFields(getFieldsByFarmId(location.farmId));
    }
  }, [location, form]);
  
  const watchGrowerId = form.watch("growerId");
  const watchFarmId = form.watch("farmId");
  
  // Update available farms when grower changes
  useEffect(() => {
    if (watchGrowerId) {
      const farms = getFarmsByGrowerId(watchGrowerId);
      setAvailableFarms(farms);
      
      // Reset farm and field selections if the current selection is no longer valid
      const currentFarmId = form.getValues("farmId");
      if (currentFarmId && !farms.some(farm => farm.id === currentFarmId)) {
        form.setValue("farmId", "");
        form.setValue("fieldId", "");
        setAvailableFields([]);
      }
    }
  }, [watchGrowerId, form]);
  
  // Update available fields when farm changes
  useEffect(() => {
    if (watchFarmId) {
      const fields = getFieldsByFarmId(watchFarmId);
      setAvailableFields(fields);
      
      // Reset field selection if the current selection is no longer valid
      const currentFieldId = form.getValues("fieldId");
      if (currentFieldId && !fields.some(field => field.id === currentFieldId)) {
        form.setValue("fieldId", "");
      }
    }
  }, [watchFarmId, form]);
  
  // Handle creating a new grower
  const handleAddNewGrower = () => {
    if (newGrowerName.trim()) {
      const newGrower = addGrower({ name: newGrowerName.trim() });
      form.setValue("growerId", newGrower.id);
      form.setValue("farmId", "");
      form.setValue("fieldId", "");
      setAvailableFarms([]);
      setAvailableFields([]);
      setNewGrowerName("");
      setShowNewGrowerInput(false);
    }
  };
  
  // Handle creating a new farm
  const handleAddNewFarm = () => {
    if (newFarmName.trim() && watchGrowerId) {
      const newFarm = addFarm({
        name: newFarmName.trim(),
        growerId: watchGrowerId
      });
      form.setValue("farmId", newFarm.id);
      form.setValue("fieldId", "");
      setAvailableFields([]);
      setNewFarmName("");
      setShowNewFarmInput(false);
    }
  };
  
  // Handle creating a new field
  const handleAddNewField = () => {
    if (newFieldName.trim() && watchFarmId && !isNaN(Number(newFieldAcres))) {
      const newField = addField({
        name: newFieldName.trim(),
        farmId: watchFarmId,
        acres: Number(newFieldAcres)
      });
      form.setValue("fieldId", newField.id);
      setNewFieldName("");
      setNewFieldAcres("0");
      setShowNewFieldInput(false);
    }
  };
  
  const onSubmit = (values: z.infer<typeof locationSchema>) => {
    // Ensure all required properties are present and non-optional
    const locationData: Omit<Location, "id"> = {
      growerId: values.growerId,
      farmId: values.farmId,
      fieldId: values.fieldId,
      defaultAssigneeId: values.defaultAssigneeId,
    };
    onSave(locationData);
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{location ? "Edit Location" : "Add Location"}</DialogTitle>
          <DialogDescription>
            {location ? "Update the location details" : "Add a new location for scouting"}
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="growerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Grower</FormLabel>
                  <div className="space-y-2">
                    {!showNewGrowerInput ? (
                      <Select 
                        onValueChange={(value) => {
                          if (value === "add-new") {
                            setShowNewGrowerInput(true);
                          } else {
                            field.onChange(value);
                          }
                        }} 
                        defaultValue={field.value}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a grower" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="add-new" className="text-primary flex items-center">
                            <Plus className="mr-2 h-4 w-4" /> Add new grower
                          </SelectItem>
                          {getAllGrowerOptions().map(grower => (
                            <SelectItem key={grower.id} value={grower.id}>
                              {grower.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="Enter grower name"
                          value={newGrowerName}
                          onChange={(e) => setNewGrowerName(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="button" onClick={handleAddNewGrower} size="sm">Add</Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowNewGrowerInput(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="farmId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Farm</FormLabel>
                  <div className="space-y-2">
                    {!showNewFarmInput ? (
                      <Select 
                        onValueChange={(value) => {
                          if (value === "add-new") {
                            setShowNewFarmInput(true);
                          } else {
                            field.onChange(value);
                          }
                        }} 
                        defaultValue={field.value}
                        value={field.value}
                        disabled={!form.watch("growerId")}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a farm" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="add-new" className="text-primary flex items-center">
                            <Plus className="mr-2 h-4 w-4" /> Add new farm
                          </SelectItem>
                          {availableFarms.map(farm => (
                            <SelectItem key={farm.id} value={farm.id}>
                              {farm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Input
                          placeholder="Enter farm name"
                          value={newFarmName}
                          onChange={(e) => setNewFarmName(e.target.value)}
                          className="flex-1"
                        />
                        <Button type="button" onClick={handleAddNewFarm} size="sm">Add</Button>
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowNewFarmInput(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="fieldId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Field</FormLabel>
                  <div className="space-y-2">
                    {!showNewFieldInput ? (
                      <Select 
                        onValueChange={(value) => {
                          if (value === "add-new") {
                            setShowNewFieldInput(true);
                          } else {
                            field.onChange(value);
                          }
                        }} 
                        defaultValue={field.value}
                        value={field.value}
                        disabled={!form.watch("farmId")}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a field" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="add-new" className="text-primary flex items-center">
                            <Plus className="mr-2 h-4 w-4" /> Add new field
                          </SelectItem>
                          {availableFields.map(field => (
                            <SelectItem key={field.id} value={field.id}>
                              {field.name} ({field.acres} acres)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Input
                            placeholder="Enter field name"
                            value={newFieldName}
                            onChange={(e) => setNewFieldName(e.target.value)}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            placeholder="Enter acres"
                            type="number"
                            value={newFieldAcres}
                            onChange={(e) => setNewFieldAcres(e.target.value)}
                            className="flex-1"
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button type="button" onClick={handleAddNewField} size="sm">Add</Button>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm" 
                            onClick={() => setShowNewFieldInput(false)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="defaultAssigneeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Assignee</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an assignee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {mockTeam.map(member => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.firstName} {member.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default LocationDialog;
